import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import SocialAuthButtons from "./SocialAuthButtons.jsx";
import TurnstileWidget, { TURNSTILE_SITE_KEY } from "./TurnstileWidget.jsx";
import { localizeMessage } from '../utils/toast.js';
import { MARKET } from '../utils/market.js';

// Persisted on every change so a reload or a back/forward navigation doesn't lose what's
// been typed — see the matching load/save effects below. Password fields are deliberately
// excluded: they'd otherwise sit in sessionStorage as plaintext for the rest of the tab's life.
const DRAFT_KEY = "playerRegDraft";
const defaultFormData = { name: "", surname: "", email: "", phone: "", password: "", confirmPassword: "" };

// Guards every registration/social-auth network call so a slow or silently-failing
// request (e.g. Cloudflare Turnstile hanging) can't leave the submit button stuck in
// a permanently-disabled loading state with no feedback — see PlayerReg audit notes.
const SUBMIT_TIMEOUT_MS = 20000;
const withTimeout = (promise, ms, timeoutMessage) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(timeoutMessage)), ms)),
  ]);

function PlayerReg() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, socialAuth } = useAuth();
  const [formData, setFormData] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY));
      return saved ? { ...defaultFormData, ...saved, password: "", confirmPassword: "" } : defaultFormData;
    } catch {
      return defaultFormData;
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState("");
  // Kept as two independent flags — the email/password registration submit and the
  // Google/Facebook social-auth submit are separate async flows and must not be able
  // to get each other stuck (see audit: a hung email registration was observed leaving
  // the "Sign in with Google" button showing its in-progress state indefinitely).
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    const { password, confirmPassword, ...rest } = formData;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
  }, [formData]);

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 20 && /^[a-zA-ZԱ-Ֆա-ֆ\s]*$/.test(value)) {
      setFormData({ ...formData, [e.target.name]: value });
      setError("");
    }
  };
  const handleEmailChange = (e) => { setFormData({ ...formData, email: e.target.value }); setError(""); };
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\+?[0-9]*$/.test(value) && (value === "" || value === "+" || !value.startsWith("+0")) && value.length <= 15) {
      setFormData({ ...formData, phone: value }); setError("");
    }
  };
  const handlePasswordChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); setError(""); };

  const validateForm = () => {
    if (!formData.name || !formData.surname || !formData.email || !formData.password || !formData.confirmPassword) return t('authPages.fillRequired');
    if (formData.name.length < 2 || formData.name.length > 20) return t('authPages.nameLength');
    if (formData.surname.length < 2 || formData.surname.length > 20) return t('authPages.surnameLength');
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) return t('authPages.invalidEmail');
    const domain = formData.email.split('@')[1];
    const invalidDomains = ['test.com', 'example.com', 'mailinator.com', 'tempmail.com', 'fake.com'];
    if (invalidDomains.includes(domain?.toLowerCase())) return t('authPages.useRealEmail');
    if (formData.phone && formData.phone.trim() !== "") {
      const phoneRegex = /^(\+374|0)\d{8}$/;
      if (!phoneRegex.test(formData.phone)) return t('authPages.invalidPhone');
    }
    if (formData.password.length < 8 || formData.password.length > 20) return t('authPages.passwordLength');
    const passwordHasLetter = /[A-Za-z]/.test(formData.password);
    const passwordHasNumber = /\d/.test(formData.password);
    if (!passwordHasLetter || !passwordHasNumber) return t('authPages.passwordLetterNumber');
    const weakPasswords = ['password', '123456', 'qwerty', 'admin', 'letmein', 'welcome', 'monkey'];
    if (weakPasswords.includes(formData.password.toLowerCase())) return t('authPages.passwordTooWeak');
    if (formData.password !== formData.confirmPassword) return t('authPages.passwordsNoMatch');
    if (!agreeToTerms) return t('authPages.mustAgreeTerms');
    if (TURNSTILE_SITE_KEY && !captchaToken) return t('authPages.completeCaptcha');
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const validationError = validateForm();
    if (validationError) { setError(validationError); setLoading(false); return; }
    try {
      const registrationPayload = {
        name: formData.name.trim(), surname: formData.surname.trim(),
        email: formData.email.toLowerCase().trim(), phone: formData.phone.trim() || null,
        password: formData.password, userType: "player", role: "user",
        username: formData.email.split('@')[0].toLowerCase(),
        isActive: true, registrationDate: new Date().toISOString(),
        captchaToken, market: MARKET,
      };
      const result = await withTimeout(
        register(registrationPayload),
        SUBMIT_TIMEOUT_MS,
        t('authPages.registrationTimeout')
      );
      if (result && result.success) {
        sessionStorage.removeItem(DRAFT_KEY);
        if (result.requiresVerification) {
          navigate("/verify-email", { replace: true, state: { email: registrationPayload.email } });
        } else {
          setTimeout(() => navigate("/player-interests", { replace: true }), 100);
        }
      } else {
        setError(result?.error || result?.message || t('authPages.registrationFailed'));
      }
    } catch (err) {
      // Covers both a genuine rejection from register() and the timeout above —
      // either way the button must come back to a normal, retryable state.
      setError(err.message || t('authPages.registrationError'));
    }
    setLoading(false);
  };

  const handleSocialRegister = async ({ provider, accessToken }) => {
    setSocialLoading(true);
    setError("");
    try {
      const result = await withTimeout(
        socialAuth({
          provider,
          accessToken,
          mode: "register",
          userType: "player",
          market: MARKET,
          phone: formData.phone.trim(),
          redirectTo: "/player-interests",
        }),
        SUBMIT_TIMEOUT_MS,
        t('authPages.registrationTimeout')
      );

      if (result?.success) {
        sessionStorage.removeItem(DRAFT_KEY);
        navigate("/player-interests", { replace: true });
      } else {
        setError(result?.error || t('authPages.socialRegFailed'));
      }
    } catch (err) {
      setError(err.message || t('authPages.socialRegFailed'));
    } finally {
      setSocialLoading(false);
    }
  };

  const renderLabel = (text, isRequired = true) => (
    <>{text}{isRequired && <span className="required-asterisk">*</span>}</>
  );

  // Shared input style
  const inputStyle = {
    width: "100%",
    padding: isMobile ? "10px 14px" : "12px 15px",
    borderRadius: "10px",
    border: "1.5px solid #ddd",
    fontSize: isMobile ? "14px" : "15px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
    fontFamily: "inherit",
  };

  const labelStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    fontSize: isMobile ? "13px" : "14px",
    fontWeight: 600,
    color: "#444",
  };

  const EyeBtn = ({ show, toggle }) => (
    <button type="button" onClick={toggle}
      style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
      <img src={show ? "/nkarner/hide.png" : "/nkarner/view.png"} alt={t('authPages.togglePasswordVisibility')} style={{ width: 18, height: 18, objectFit: "contain" }} />
    </button>
  );

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f5f7f0",
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: "center",
      padding: isMobile ? "20px 16px 100px" : "40px 20px",
      boxSizing: "border-box",
    }}>
      <div style={{
        background: "white",
        borderRadius: isMobile ? "20px" : "15px",
        padding: isMobile ? "24px 18px 28px" : "40px",
        width: "100%",
        maxWidth: isMobile ? "420px" : "500px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
      }}>
        <h2 style={{ fontSize: isMobile ? "20px" : "26px", fontWeight: 700, color: "#2b2b52", textAlign: "center", margin: "0 0 18px" }}>
          {t('authPages.createAccount')}
        </h2>

        {error && (
          <div style={{ background: "#ffebee", color: "#c62828", padding: "10px 14px", borderRadius: "10px", fontSize: "12px", marginBottom: "14px", border: "1px solid #ffcdd2", lineHeight: 1.4 }}>
            {localizeMessage(error)}
          </div>
        )}

        <form onSubmit={handleRegister} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <SocialAuthButtons onAuth={handleSocialRegister} disabled={loading || socialLoading} mode="register" />

          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#8a8a8a", fontSize: 11 }}>
            <div style={{ flex: 1, height: 1, background: "#e6ece8" }} />
            <span>{t('authPages.orRegisterEmail')}</span>
            <div style={{ flex: 1, height: 1, background: "#e6ece8" }} />
          </div>

          {/* Name + Surname row on mobile */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr", gap: "12px" }}>
            <label style={labelStyle}>
              {renderLabel(t('authPages.name'))}
              <div style={{ position: "relative" }}>
                <input type="text" name="name" value={formData.name} onChange={handleNameChange}
                  placeholder={t('authPages.name')} maxLength="20" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                <span style={{ position: "absolute", right: 8, bottom: 6, fontSize: "9px", color: "#aaa" }}>{formData.name.length}/20</span>
              </div>
            </label>
            <label style={labelStyle}>
              {renderLabel(t('authPages.surname'))}
              <div style={{ position: "relative" }}>
                <input type="text" name="surname" value={formData.surname} onChange={handleNameChange}
                  placeholder={t('authPages.surname')} maxLength="20" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                <span style={{ position: "absolute", right: 8, bottom: 6, fontSize: "9px", color: "#aaa" }}>{formData.surname.length}/20</span>
              </div>
            </label>
          </div>

          <label style={labelStyle}>
            {renderLabel(t('authPages.email'))}
            <input type="email" name="email" value={formData.email} onChange={handleEmailChange}
              placeholder="example@gmail.com" style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
          </label>

          <label style={labelStyle}>
            {renderLabel(t('authPages.phoneOptional'), false)}
            <input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange}
              placeholder="+374 XX XXX XXX" maxLength="15" style={inputStyle}
              onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
            <span style={{ fontSize: "10px", color: "#aaa" }}>{t('authPages.phoneExample')}</span>
          </label>

          <label style={labelStyle}>
            {renderLabel(t('authPages.password'))}
            <div style={{ position: "relative" }}>
              <input type={showPassword ? "text" : "password"} name="password" value={formData.password}
                onChange={handlePasswordChange} placeholder={t('authPages.passwordPlaceholderHint')}
                minLength="8" maxLength="20" style={{ ...inputStyle, padding: isMobile ? "10px 40px 10px 14px" : "12px 40px 12px 15px" }}
                onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              <EyeBtn show={showPassword} toggle={() => setShowPassword(!showPassword)} />
            </div>
            {/* Password requirements — compact on mobile */}
            {formData.password && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "4px" }}>
                {[
                  { test: formData.password.length >= 8, text: t('authPages.charsMin') },
                  { test: /[A-Za-z]/.test(formData.password), text: t('authPages.letter') },
                  { test: /\d/.test(formData.password), text: t('authPages.number') },
                ].map((r, i) => (
                  <span key={i} style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "999px", background: r.test ? "#e8f5e9" : "#fce4ec", color: r.test ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                    {r.test ? "✓" : "✗"} {r.text}
                  </span>
                ))}
              </div>
            )}
          </label>

          <label style={labelStyle}>
            {renderLabel(t('authPages.confirmPassword'))}
            <div style={{ position: "relative" }}>
              <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={formData.confirmPassword}
                onChange={handlePasswordChange} placeholder={t('authPages.confirmYourPassword')}
                style={{ ...inputStyle, padding: isMobile ? "10px 40px 10px 14px" : "12px 40px 12px 15px" }}
                onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              <EyeBtn show={showConfirmPassword} toggle={() => setShowConfirmPassword(!showConfirmPassword)} />
            </div>
            {formData.confirmPassword && (
              <span style={{ fontSize: "11px", color: formData.password === formData.confirmPassword ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                {formData.password === formData.confirmPassword ? `✓ ${t('authPages.passwordsMatch')}` : `✗ ${t('authPages.passwordsDontMatchShort')}`}
              </span>
            )}
          </label>

          {/* Terms */}
          <label style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: "10px", cursor: "pointer", fontSize: "12px", color: "#555", fontWeight: 400 }}>
            <input type="checkbox" checked={agreeToTerms} onChange={e => setAgreeToTerms(e.target.checked)}
              style={{ width: "auto", marginTop: "2px", flexShrink: 0 }} />
            <span>
              {t('authPages.agreeToTerms')}{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#32614d", fontWeight: 600 }}>{t('authPages.termsConditions')}</a>
              {" "}{t('authPages.and')}{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#32614d", fontWeight: 600 }}>{t('authPages.privacyPolicy')}</a>
            </span>
          </label>

          <TurnstileWidget onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} />

          <button type="submit"
            disabled={loading || socialLoading || !agreeToTerms || (!!TURNSTILE_SITE_KEY && !captchaToken)}
            style={{
              width: "100%", padding: isMobile ? "13px" : "14px", borderRadius: "10px", border: "none",
              background: (!agreeToTerms || loading || socialLoading || (TURNSTILE_SITE_KEY && !captchaToken)) ? "#ccc" : "linear-gradient(135deg, #32614d, #2a5240)",
              color: "white", fontSize: isMobile ? "15px" : "16px", fontWeight: 700,
              cursor: (!agreeToTerms || loading || socialLoading || (TURNSTILE_SITE_KEY && !captchaToken)) ? "not-allowed" : "pointer",
              transition: "opacity 0.2s", marginTop: "4px", fontFamily: "inherit",
            }}
          >
            {loading ? t('authPages.registering') : t('authPages.registerBtn')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PlayerReg;
